import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { useUpsertCommissionMutation, useDeleteCommissionMutation } from "../../salesApi";

const today = () => new Date().toISOString().slice(0, 10);

// Record (or correct, or reverse) the commission on a CLOSED order.
//
// There is no paid/unpaid flag to toggle: App_SalesOrderCommission either
// holds a row for this order or it does not, and the API derives
// IsCommissionPaid from exactly that. So PUT = paid, DELETE = not paid.
// Deliberately no new status value was introduced - the existing shape
// already expresses everything the screen needs.
//
// Shared by the Commission worklist and the Order Detail card so the two can
// never drift on validation or wording.
const CommissionModal = ({ show, onClose, order }) => {
  const [upsertCommission, { isLoading: isSaving }] = useUpsertCommissionMutation();
  const [deleteCommission, { isLoading: isReversing }] = useDeleteCommissionMutation();

  const [amount, setAmount] = useState("");
  const [paidDate, setPaidDate] = useState(today());
  const [chequeNumber, setChequeNumber] = useState("");
  const [remarks, setRemarks] = useState("");

  const isPaid = Boolean(order?.isCommissionPaid);

  useEffect(() => {
    if (!show || !order) return;
    // Editing an already-recorded commission opens on its real values, so a
    // correction is a correction rather than a silent re-entry from blank.
    setAmount(order.commissionAmount != null ? String(order.commissionAmount) : "");
    setPaidDate(order.commissionPaidDate ? String(order.commissionPaidDate).slice(0, 10) : today());
    setChequeNumber(order.commissionChequeNumber || "");
    setRemarks(order.commissionRemarks || "");
  }, [show, order]);

  const handleSave = async () => {
    const parsed = Number(amount);
    if (!amount.trim() || !Number.isFinite(parsed) || parsed <= 0) {
      return toast.error("Enter a commission amount greater than zero");
    }
    if (!paidDate) return toast.error("Paid date is required");
    try {
      await upsertCommission({
        recordGuid: order.recordGuid,
        amount: parsed,
        paidDate,
        chequeNumber: chequeNumber.trim() || null,
        remarks: remarks.trim() || null,
      }).unwrap();
      toast.success(isPaid ? "Commission updated" : "Commission recorded");
      onClose();
    } catch (e) {
      toast.error(e?.message || "Could not save the commission");
    }
  };

  const handleReverse = async () => {
    if (!window.confirm("Reverse this commission? The order goes back to Not Paid.")) return;
    try {
      await deleteCommission(order.recordGuid).unwrap();
      toast.success("Commission reversed");
      onClose();
    } catch (e) {
      toast.error(e?.message || "Could not reverse the commission");
    }
  };

  const busy = isSaving || isReversing;

  return (
    <Modal show={show} onHide={busy ? undefined : onClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{isPaid ? "Edit Commission" : "Record Commission"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">
          {order?.orderNumber} &middot; {order?.customerName}
          {order?.salespersonName ? ` · ${order.salespersonName}` : ""}
          {order?.totalAmount != null ? ` · order value ${Number(order.totalAmount).toFixed(2)}` : ""}
        </p>
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">Commission Amount <span className="text-danger">*</span></label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="form-control"
              value={amount}
              autoFocus
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Paid Date <span className="text-danger">*</span></label>
            <input type="date" className="form-control" value={paidDate} onChange={(e) => setPaidDate(e.target.value)} />
          </div>
        </div>
        <div className="mb-3">
          <label className="form-label">Cheque / Reference Number <span className="text-muted">(optional)</span></label>
          <input type="text" className="form-control" value={chequeNumber} onChange={(e) => setChequeNumber(e.target.value)} />
        </div>
        <div className="mb-1">
          <label className="form-label">Remarks <span className="text-muted">(optional)</span></label>
          <textarea className="form-control" rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <div>
          {isPaid && (
            <Button variant="outline-danger" onClick={handleReverse} disabled={busy}>
              {isReversing ? "Reversing..." : "Reverse"}
            </Button>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={busy}>
            {isSaving ? "Saving..." : isPaid ? "Save Changes" : "Mark Paid"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CommissionModal;
